import { QueryParameters as NavigationCardQueryParams } from "@faire/web-api/api/navigation/get";
import { useQuery as useNavigationCardDataQuery } from "@faire/web-api/api/navigation/get-hooks";
import { useQuery as useNavigationCardHashcodeQuery } from "@faire/web-api/api/navigation/hashcode/get-hooks";
import { ClientPlatformType } from "@faire/web-api/indigofair/data/ClientPlatformType";
import { DEFAULT_LOCALE_KEY } from "@faire/web/common/consts/DEFAULT_LOCALE";
import { buildLocale } from "@faire/web/common/localization/utils";
import { logError } from "@faire/web/common/logging";
import { useEffect, useMemo } from "react";

import { getLocaleKey } from "@faire/retailer/serialized-data/getLocaleKey";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";
import { useSettingGlobalNavigationRedesign } from "@faire/retailer/settings/useSettingGlobalNavigationRedesign";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

export const useNavigationCardQuery = (
  clientPlatformType: ClientPlatformType
) => {
  const { retailerToken } = useRetailerStore(["retailerToken"]);
  const localeKey = getLocaleKey();
  const locale = buildLocale(localeKey ?? DEFAULT_LOCALE_KEY);
  const country = getSessionCountry();
  const isNavigationRedesignEnabled = useSettingGlobalNavigationRedesign();

  const {
    data: navigationCardHashCodeData,
    isError: isNavigationCardHashcodeError,
    error: navigationCardHashcodeError,
    refetch: refetchHashcode,
  } = useNavigationCardHashcodeQuery(undefined, {
    enabled: isNavigationRedesignEnabled,
  });

  const navigationCardQuery = useMemo(() => {
    return NavigationCardQueryParams.build({
      retailerToken,
      locale,
      clientPlatformType: clientPlatformType,
      country,
      hashCode: navigationCardHashCodeData?.navigation_hashcode,
    });
  }, [
    clientPlatformType,
    country,
    locale,
    navigationCardHashCodeData?.navigation_hashcode,
    retailerToken,
  ]);

  const {
    data: navigationCardData,
    isError: isNavigationCardError,
    error: navigationCardError,
  } = useNavigationCardDataQuery(navigationCardQuery, {
    enabled:
      isNavigationRedesignEnabled &&
      !!navigationCardHashCodeData?.navigation_hashcode,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isNavigationCardError) {
      logError(navigationCardError);
    }
  }, [isNavigationCardError, navigationCardError]);

  useEffect(() => {
    if (isNavigationCardHashcodeError) {
      logError(navigationCardHashcodeError);
    }
  }, [isNavigationCardHashcodeError, navigationCardHashcodeError]);

  return {
    navigationCardData,
    refetchHashcode,
  };
};
